/* eslint-disable react/require-default-props */
import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import Icon from '../../icon/Icon';

interface Option {
    value: string;
    text: string;
}

interface CheckboxDropdownProps {
    id: string;
    label: string;
    options: Option[];
    value: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    isTouched?: boolean;
    invalidFeedback?: string;
    isValid?: boolean;
}

const CheckboxDropdown: React.FC<CheckboxDropdownProps> = ({
    id,
    label,
    options,
    value,
    onChange,
    placeholder = 'Select options',
    disabled = false,
    isTouched = false,
    invalidFeedback = '',
    isValid = false,
}) => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const [openUp, setOpenUp] = useState(false);

    const toggleOpen = () => {
        if (disabled) return;

        if (!open && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();

            const dropdownHeight = 200; // Same as your max-height
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            setOpenUp(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
        }

        setOpen((prev) => !prev);
    };

    const handleChange = (optionValue: string) => {
        if (value.includes(optionValue)) {
            onChange(value.filter((v) => v !== optionValue));
        } else {
            onChange([...value, optionValue]);
        }
    };

    const handleSelectAll = () => {
        const filteredValues = filteredOptions.map((opt) => opt.value);
        const allFilteredSelected = filteredValues.every((v) => value.includes(v));

        if (allFilteredSelected) {
            onChange(value.filter((v) => !filteredValues.includes(v)));
        } else {
            const newValues = [...value, ...filteredValues.filter((v) => !value.includes(v))];
            onChange(newValues);
        }
    };

    const filteredOptions = options.filter((opt) =>
        opt.text.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    const filteredValues = filteredOptions.map((opt) => opt.value);
    const allFilteredSelected = filteredValues.length > 0 && filteredValues.every((v) => value.includes(v));
    const someFilteredSelected = filteredValues.some((v) => value.includes(v)) && !allFilteredSelected;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const selectedText =
        value.length === 0
            ? placeholder
            : options
                .filter((opt) => value.includes(opt.value))
                .map((opt) => opt.text)
                .join(', ');

    return (
        <div className="mb-3 position-relative" ref={containerRef} style={{ zIndex: 1050 }}>
            <span className="form-label" id={`${id}-label`}>
                {label}
            </span>

            <div
                role="button"
                tabIndex={0}
                aria-labelledby={`${id}-label`}
                aria-haspopup="listbox"
                aria-expanded={open}
                className={classNames('form-select', {
                    'is-invalid': isTouched && invalidFeedback,
                    'is-valid': isTouched && isValid,
                })}
                onClick={toggleOpen}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleOpen();
                    }
                }}
                style={{
                    minHeight: '34px',
                    marginTop: 7,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
                id={id}
            >
                {selectedText}
            </div>

            {open && !disabled && (
                <div
                    className="custom-dropdown bg-dark position-absolute"
                    style={{
                        top: openUp ? 'auto' : '100%',
                        bottom: openUp ? '100%' : 'auto',
                        marginTop: openUp ? 0 : 2,
                        marginBottom: openUp ? 2 : 0,
                    }}
                    role="listbox"
                    aria-multiselectable="true"
                >
                    <div className="form-check px-2 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                        <input
                            type="checkbox"
                            id={`${id}-check-all`}
                            className="form-check-input mt-3"
                            ref={(input) => {
                                if (input) {
                                    input.indeterminate = someFilteredSelected;
                                }
                            }}
                            checked={allFilteredSelected}
                            onChange={handleSelectAll}
                            disabled={filteredOptions.length === 0}
                        />
                        <div className="px-2 py-2 position-relative">
                            <span
                                style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#6c757d',
                                    fontSize: '0.95rem',
                                    pointerEvents: 'none',
                                }}>
                                <Icon icon='Search' size='sm' />
                            </span>
                            <input
                                type="search"
                                className="form-control form-control-sm"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                aria-label="Search options"
                                style={{ paddingLeft: '2.2rem' }}
                            />
                        </div>
                    </div>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt) => (
                            <div className="form-check px-2 py-1" key={opt.value}>
                                <input
                                    type="checkbox"
                                    id={`${id}-${opt.value}`}
                                    className="form-check-input"
                                    checked={value.includes(opt.value)}
                                    onChange={() => handleChange(opt.value)}
                                />
                                <label htmlFor={`${id}-${opt.value}`} className="form-check-label">
                                    {opt.text}
                                </label>
                            </div>
                        ))
                    ) : (
                        <div className="px-2 py-2 text-muted small">No matching options</div>
                    )}
                </div>
            )}

            {isTouched && invalidFeedback && (
                <div className="invalid-feedback d-block">{invalidFeedback}</div>
            )}
        </div>
    );
};

export default CheckboxDropdown;
/* eslint-enable react/require-default-props */